import { manufacturingFetch } from "@/lib/manufacturingClient";
import { requireSupabase } from "@/lib/supabase";
import type { ServiceOfferingDocument, ServiceOfferingDocumentKind } from "./types";

export const SERVICE_OFFERING_DOCUMENT_KIND_LABELS: Record<ServiceOfferingDocumentKind, string> = {
  order_form: "Order form",
  pricing_worksheet: "Pricing worksheet",
  guidance: "Guidance",
  other: "Other",
};

/** Suffix for inherited offering doc links, e.g. "(ShedCor)". */
export function offeringDocumentPublisherSuffix(
  publisherOrgName: string | null | undefined,
  isInherited?: boolean
): string {
  if (!isInherited || !publisherOrgName?.trim()) return "";
  return ` (${publisherOrgName.trim()})`;
}

export function offeringDocumentLinkLabel(
  doc: Pick<ServiceOfferingDocument, "kind" | "filename">,
  opts?: { publisherOrgName?: string | null; isInherited?: boolean; compact?: boolean }
): string {
  const kind = SERVICE_OFFERING_DOCUMENT_KIND_LABELS[doc.kind];
  const suffix = offeringDocumentPublisherSuffix(opts?.publisherOrgName, opts?.isInherited);
  if (opts?.compact) return `${kind}${suffix}`;
  return `${kind}${suffix} — ${doc.filename}`;
}

export const SERVICE_OFFERING_DOCUMENT_ACCEPT =
  ".pdf,.docx,.xlsx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

type ListOk = {
  ok: true;
  organization_id: string;
  offering_id: string;
  is_inherited: boolean;
  publisher_org_name: string | null;
  documents: ServiceOfferingDocument[];
};

type SignOk = {
  ok: true;
  bucket: string;
  path: string;
  token: string;
  signed_url: string;
  document_id: string;
  content_type: string;
  kind: ServiceOfferingDocumentKind;
};

type CommitOk = {
  ok: true;
  document: ServiceOfferingDocument;
  replaced_kind: ServiceOfferingDocumentKind;
};

function isListOk(x: unknown): x is ListOk {
  if (!x || typeof x !== "object") return false;
  const o = x as Record<string, unknown>;
  return o.ok === true && Array.isArray(o.documents);
}

function isSignOk(x: unknown): x is SignOk {
  if (!x || typeof x !== "object") return false;
  const o = x as Record<string, unknown>;
  return (
    o.ok === true &&
    typeof o.bucket === "string" &&
    typeof o.path === "string" &&
    typeof o.token === "string" &&
    typeof o.document_id === "string"
  );
}

function isCommitOk(x: unknown): x is CommitOk {
  if (!x || typeof x !== "object") return false;
  const o = x as Record<string, unknown>;
  return o.ok === true && typeof o.document === "object" && o.document !== null;
}

export function listServiceOfferingDocuments(args: {
  offering_id: string;
  sign?: boolean;
}) {
  const q = new URLSearchParams({ offering_id: args.offering_id });
  if (args.sign) q.set("sign", "1");
  return manufacturingFetch<ListOk>(`/api/service/offering-documents?${q.toString()}`, {
    method: "GET",
  });
}

export type UploadServiceOfferingDocumentResult =
  | { ok: true; document: ServiceOfferingDocument; replaced_kind: ServiceOfferingDocumentKind }
  | { ok: false; error: string };

export async function uploadServiceOfferingDocument(args: {
  offeringId: string;
  kind: ServiceOfferingDocumentKind;
  file: File;
}): Promise<UploadServiceOfferingDocumentResult> {
  const sb = requireSupabase();
  const contentType = (args.file.type || "application/octet-stream").toLowerCase();

  const sign = await manufacturingFetch<unknown>("/api/service/offering-documents/upload", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      op: "sign",
      offering_id: args.offeringId,
      kind: args.kind,
      content_type: contentType,
      byte_size: args.file.size,
      filename: args.file.name,
    }),
  });
  if (!sign.ok) return { ok: false, error: sign.error };
  if (!isSignOk(sign.data)) {
    return { ok: false, error: "Unexpected sign response." };
  }

  const uploadErr = await sb.storage.from(sign.data.bucket).uploadToSignedUrl(
    sign.data.path,
    sign.data.token,
    args.file,
    { contentType: sign.data.content_type, upsert: false }
  );
  if (uploadErr.error) {
    return { ok: false, error: uploadErr.error.message ?? "Upload failed." };
  }

  const commit = await manufacturingFetch<unknown>("/api/service/offering-documents/upload", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      op: "commit",
      offering_id: args.offeringId,
      document_id: sign.data.document_id,
      object_path: sign.data.path,
      content_type: sign.data.content_type,
      kind: args.kind,
      filename: args.file.name,
      byte_size: args.file.size,
    }),
  });
  if (!commit.ok) return { ok: false, error: commit.error };
  if (!isCommitOk(commit.data)) {
    return { ok: false, error: "Unexpected commit response." };
  }

  return {
    ok: true,
    document: commit.data.document,
    replaced_kind: commit.data.replaced_kind,
  };
}

export async function deleteServiceOfferingDocument(documentId: string) {
  return manufacturingFetch<{ ok: true; deleted: boolean }>(
    "/api/service/offering-documents/delete",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ op: "delete", document_id: documentId }),
    }
  );
}

export function formatOfferingDocumentSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function isListResponseOk(data: unknown): data is ListOk {
  return isListOk(data);
}
